import type { AuditorStat, QuestionStat, SectionStat, TemplateLabels } from '../../audit/analytics';
import type { KpiCell, KpiColumn } from '../../audit/kpis';
import { WINDOW_LABELS, type WindowStatus } from '../../audit/windows';
import type { Store } from '../../data/types';
import type { CsvCell } from '../../ui/csv';
import { isoToCompanyDay } from '../../ui/dates';
import { plural } from '../../ui/plural';
import { wholeScore } from '../../ui/scoreText';

/**
 * CSV rows for the Analytics tables (B6, owner rulings 2026-10-25) — built
 * from the SAME rows each table renders (filters applied), as plain values:
 * whole/one-decimal numbers (scores as whole percents like the screen —
 * owner 2026-11-02), YYYY-MM-DD dates. Pure.
 */

const one = (v: number | null) => (v === null || !Number.isFinite(v) ? null : Math.round(v * 10) / 10);
const score = (v: number | null) => (v === null || !Number.isFinite(v) ? null : wholeScore(v));

export interface HeatMapCsvRow {
  store: Store;
  /** One per column, in column order; null = not assigned to this store. */
  cells: readonly ({ status: WindowStatus; late: number } | null)[];
}

/** Compliance heat map: one column per clock, "Overdue 12d" / "Current" / "Not assigned". */
export function heatMapCsv(columnNames: readonly string[], rows: readonly HeatMapCsvRow[]): { header: string[]; rows: CsvCell[][] } {
  return {
    header: ['Store number', 'Store', 'District', ...columnNames],
    rows: rows.map((r) => [
      r.store.storeNumber,
      r.store.name,
      r.store.district,
      ...r.cells.map((c) => (c === null ? 'Not assigned' : `${WINDOW_LABELS[c.status]}${c.late > 0 ? ` ${plural(c.late, 'day')}` : ''}`)),
    ]),
  };
}

export const SECTIONS_CSV_HEADER = ['Section', 'Average score', 'Audits'] as const;

export function sectionsCsvRows(sections: readonly SectionStat[]): CsvCell[][] {
  return sections.map((s) => [s.title, score(s.avg), s.audits]);
}

export const QUESTIONS_CSV_HEADER = ['Question', 'Section', 'Failed %', 'Failed', 'Checked', 'Audits'] as const;

export function questionsCsvRows(questions: readonly QuestionStat[], labels: TemplateLabels): CsvCell[][] {
  return questions.map((q) => {
    const l = labels.question.get(q.questionId);
    return [l?.text ?? q.questionId, l?.section ?? '', Math.round(q.failRate * 100), q.failed, q.checked, q.audits];
  });
}

export const AUDITORS_CSV_HEADER = ['Person', 'Email', 'Audits', 'Average score', 'Stores', 'Critical fails', 'Last audit'] as const;

/** `label` = the name the table shows for the person who conducted the audits (directory name, store label, else the address). */
export function auditorsCsvRows(auditors: readonly AuditorStat[], label: (a: AuditorStat) => string): CsvCell[][] {
  return auditors.map((a) => [label(a), a.auditorUpn, a.audits, score(a.avgScore), a.stores, a.criticalFails, isoToCompanyDay(a.lastSubmitted)]);
}

/** KPI tables: each KPI as its average + its change against the previous range (percent changes in points). */
export function kpiCsvHeader(first: readonly string[], countLabel: string, columns: readonly KpiColumn[]): string[] {
  return [
    ...first,
    countLabel,
    ...columns.flatMap((c) => [
      c.type === 'percent' ? `${c.label} (%)` : c.label,
      `${c.label} — change${c.type === 'percent' ? ' (pts)' : ''}`,
    ]),
  ];
}

export function kpiCsvCells(t: { visits: number; cells: ReadonlyMap<string, KpiCell> }, columns: readonly KpiColumn[]): CsvCell[] {
  return [t.visits, ...columns.flatMap((c) => [one(t.cells.get(c.id)?.avg ?? null), one(t.cells.get(c.id)?.change ?? null)])];
}
